import { Box, Flex, HStack, VStack, Text, Divider } from "@chakra-ui/react";
import { cloneDeep, isArray } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { useSelector } from "react-redux";
import { FilterGroupBox } from "./FilterGroupBox";
import { RiMailCheckLine } from "react-icons/ri";
import { FILTER_TYPE } from "../../../../../common/constants/campaign";
import { getOperatorDetails } from "../../../../../common/helper/commonHelper";
import {
  DynamicListChild,
  DynamicListChildL1,
  CONNECTOR,
  ValueTypes,
  DynamicListChildL2,
  OperatorType,
  TYPE,
} from "../../../../../common/types/campaign";
import {
  MarketingActivityArgument,
  MarketingActivity,
} from "../../../../../common/types/person";
import { LinkFilter } from "../../../../../components/dynamic-list/CommonFilterRows";
import { DynamicListContext } from "../../../../../components/dynamic-list/DynamicList";
import {
  ValueSelectFields,
  DynamicListValueFields,
} from "../../../../../components/dynamic-list/DynamicListValueFields";
import IButton from "../../../../../components/IButton";
import RemoveRowButton from "../../../../../components/RemoveRowButton";
import { useAppDispatch } from "../../../../../store";
import { validateFilterGroup } from "../../helper/validationHelper";
import {
  getMarketingActivities,
  getTemplatesOfCampaign,
  selectCampaign,
} from "../../campaignSlice";

enum CHILD_FILTER_TYPE {
  TIME_FRAME = "event_date",
  FREQUENCY = "frequency",
  LINK_CLICKED = "url",
}

enum PROPERTY_FILTER_ID {
  TEMPLATE = "template_id",
  CAMPAIGN = "campaign_id",
  REASON = "reason",
}

const IS_OPERATOR = "asset_is_equal";

const VALUE_ALL = "all";

const templateListInit = [{ label: "All templates", value: VALUE_ALL }];
const campaignListInit = [{ label: "All journeys", value: VALUE_ALL }];

const templateRow: DynamicListChildL2 = {
  type: TYPE.EXPRESSION,
  filter_type: FILTER_TYPE.MARKETING_ACTIVITY,
  filter: PROPERTY_FILTER_ID.TEMPLATE,
  property: null,
  operator: null,
  value: [""],
  connector: CONNECTOR.AND,
};

function PropertyGroupRow({
  data,
  onChange,
  argumentData,
  argumentSelectList,
  isReadOnly,
}: {
  data: DynamicListChildL2;
  onChange: (data: DynamicListChildL2) => void;
  argumentData?: MarketingActivityArgument;
  argumentSelectList?: { label: string; value: string }[];
  isReadOnly?: boolean;
}) {
  const {
    dynamicList: {
      operators: { data: operators },
    },
    activeErrorCheck,
  } = useSelector(selectCampaign);

  const operatorDetails = useMemo(
    () => getOperatorDetails(data.operator, operators, argumentData?.type),
    [data.operator, operators, argumentData]
  );

  function setOperator(operator: string) {
    const dataCopy = cloneDeep(data);
    dataCopy.operator = operator;
    dataCopy.value = [];
    dataCopy.validation_error = "";
    onChange(dataCopy);
  }

  function handleValueChange(val: ValueTypes) {
    const dataCopy = cloneDeep(data);
    if (
      operatorDetails?.arguments_types?.length &&
      val.length &&
      val.some((x) => x === "")
    ) {
      dataCopy.validation_error = "Invalid values";
    } else {
      dataCopy.validation_error = "";
    }
    dataCopy.value = val;
    onChange(dataCopy);
  }

  const wrapperStyle = isReadOnly
    ? {
        p: "0",
        spacing: "1",
      }
    : {
        spacing: "3",
        py: "2",
        px: "3",
        gridGap: "2",
      };

  if (argumentData) {
    return (
      <HStack alignItems="center" wrap="wrap" {...wrapperStyle}>
        <Text fontSize="sm" textAlign="right">
          {argumentData.display}
        </Text>

        {operators && (
          <Box>
            <ValueSelectFields
              options={Object.values(operators[argumentData.type]).map(
                (op) => ({
                  label: op.display,
                  value: op.id,
                })
              )}
              value={(data.operator as string) || ""}
              onChange={setOperator}
              validationError={activeErrorCheck ? data.validation_error : ""}
              isReadOnly={isReadOnly}
            />
          </Box>
        )}
        {data.operator && operatorDetails && (
          <HStack>
            <DynamicListValueFields
              value={data.value}
              onChange={handleValueChange}
              argumentTypes={operatorDetails.arguments_types}
              argumentSelectList={
                data.operator === IS_OPERATOR ? argumentSelectList : undefined
              }
              noOfArguments={operatorDetails.arguments}
              helperText={operatorDetails.display_2}
              validationError={activeErrorCheck ? data.validation_error : ""}
              isReadOnly={isReadOnly}
            />
          </HStack>
        )}
      </HStack>
    );
  }

  return <></>;
}

function PropertyGroup({
  mainFilter,
  data,
  onRemove,
  onChange,
  isReadOnly,
}: {
  mainFilter: ValueTypes | null;
  data: DynamicListChildL1;
  onRemove: () => void;
  onChange: (data: DynamicListChildL1) => void;
  isReadOnly?: boolean;
}) {
  const dispatch = useAppDispatch();

  const {
    dynamicList: {
      marketingEventNamesList: { data: marketingEventNamesList },
      templateListOfCampaign,
    },
    totalCampaignList,
    campaignDetails,
  } = useSelector(selectCampaign);

  const [templateList, setTemplateList] =
    useState<{ label: string; value: string }[]>(templateListInit);

  const campaignFilter = useMemo(
    () =>
      data.children?.find(
        (child) => child.filter === PROPERTY_FILTER_ID.CAMPAIGN
      ),
    [data.children]
  );
  const templateFilter = useMemo(
    () =>
      data.children?.find(
        (child) => child.filter === PROPERTY_FILTER_ID.TEMPLATE
      ),
    [data.children]
  );
  const reasonFilter = useMemo(
    () =>
      data.children?.find(
        (child) => child.filter === PROPERTY_FILTER_ID.REASON
      ),
    [data.children]
  );

  useEffect(() => {
    const campaignId = campaignFilter?.value[0];
    if (
      campaignFilter?.operator === IS_OPERATOR &&
      campaignId &&
      campaignId !== VALUE_ALL
    ) {
      const templates =
        templateListOfCampaign[campaignId]?.data.map((template) => {
          return {
            value: template.template_id,
            label: template.name,
          };
        }) ?? [];
      setTemplateList(templates);
    }
  }, [templateListOfCampaign, campaignFilter?.value, campaignFilter?.operator]);

  const campaignList: { label: string; value: string }[] = useMemo(() => {
    const data = totalCampaignList.data
      .filter(
        (item) =>
          campaignDetails.data.campaign_id !== item.campaign_id &&
          (item.activated_at || item.deactivated_at)
      )
      .map((item) => {
        return {
          label: item.name,
          value: item.campaign_id,
        };
      });
    return [...campaignListInit, ...data];
  }, [campaignDetails.data.campaign_id, totalCampaignList]);

  useEffect(() => {
    const campaignId = campaignFilter?.value[0];
    if (
      campaignFilter?.operator === IS_OPERATOR &&
      campaignId &&
      campaignId !== VALUE_ALL
    )
      dispatch(getTemplatesOfCampaign(campaignId as string));
  }, [dispatch, campaignFilter?.value, campaignFilter?.operator]);

  function onChangeHandler(
    newData: DynamicListChildL2,
    type: PROPERTY_FILTER_ID
  ) {
    if (data.children) {
      const dataCopy = cloneDeep(data);
      const index = dataCopy.children?.findIndex(
        (child) => child.filter === type
      );
      if (index !== undefined) {
        (dataCopy.children as DynamicListChildL2[])[index] = newData;
        if (type === PROPERTY_FILTER_ID.CAMPAIGN) {
          const templateFilterIndex = dataCopy.children?.findIndex(
            (child) => child.filter === PROPERTY_FILTER_ID.TEMPLATE
          );
          if (templateFilterIndex !== undefined) {
            (dataCopy.children as DynamicListChildL2[])[templateFilterIndex] =
              templateRow;
          }
        }
      }
      onChange(dataCopy);
    }
  }

  function getArgumentDetails(type: string) {
    return mainFilter
      ? marketingEventNamesList
          ?.find((x) => x.id === mainFilter[0])
          ?.arguments.find((x) => x.id === type)
      : undefined;
  }

  return (
    <Flex w="100%" flexWrap="nowrap" justifyContent="space-between">
      <VStack spacing="0" w="100%" alignItems="flex-start">
        {campaignFilter && (
          <PropertyGroupRow
            onChange={(data) =>
              onChangeHandler(data, PROPERTY_FILTER_ID.CAMPAIGN)
            }
            data={campaignFilter}
            argumentData={getArgumentDetails(PROPERTY_FILTER_ID.CAMPAIGN)}
            argumentSelectList={campaignList}
            isReadOnly={isReadOnly}
          />
        )}
        {templateFilter && (
          <PropertyGroupRow
            onChange={(data) =>
              onChangeHandler(data, PROPERTY_FILTER_ID.TEMPLATE)
            }
            data={templateFilter}
            argumentData={getArgumentDetails(PROPERTY_FILTER_ID.TEMPLATE)}
            argumentSelectList={templateList}
            isReadOnly={isReadOnly}
          />
        )}
        {reasonFilter && (
          <PropertyGroupRow
            onChange={(data) =>
              onChangeHandler(data, PROPERTY_FILTER_ID.REASON)
            }
            data={reasonFilter}
            argumentData={getArgumentDetails(PROPERTY_FILTER_ID.REASON)}
            isReadOnly={isReadOnly}
          />
        )}
      </VStack>
      {!isReadOnly && <RemoveRowButton onClick={onRemove} />}
    </Flex>
  );
}

function ActivitySelector({
  marketingActivityList,
  onChange,
  value,
  validationError,
  isReadOnly,
}: {
  marketingActivityList: MarketingActivity[];
  onChange: (value: string) => void;
  value: string | null;
  validationError?: string;
  isReadOnly?: boolean;
}) {
  return (
    <HStack alignItems="center" spacing="3">
      {!isReadOnly && <Text fontSize="sm">Marketing event</Text>}
      <Box id="marketing-activity-selector">
        <ValueSelectFields
          options={marketingActivityList?.map((x) => ({
            label: x.display,
            value: x.id,
          }))}
          value={value || ""}
          onChange={onChange}
          validationError={validationError}
          isReadOnly={isReadOnly}
        />
      </Box>
    </HStack>
  );
}

function PropertyGroupContainer({
  data,
  onChange,
  isReadOnly,
}: {
  data: DynamicListChildL1[];
  onChange: (data: DynamicListChildL1[]) => void;
  isReadOnly?: boolean;
}) {
  function onRemovePropertyGroup(index: number) {
    const dataCopy = cloneDeep(data);
    dataCopy.splice(index, 1);
    onChange(dataCopy);
  }

  function onChangePropertyGroup(
    updateData: DynamicListChildL1,
    index: number
  ) {
    const dataCopy = cloneDeep(data);
    dataCopy[index] = updateData;
    onChange(dataCopy);
  }

  return (
    <>
      {data.map(
        (item, index) =>
          item.type === TYPE.GROUP && (
            <>
              {!isReadOnly && <Divider />}
              <Flex w="100%">
                <PropertyGroup
                  mainFilter={data[0].value}
                  data={item}
                  onRemove={() => onRemovePropertyGroup(index)}
                  onChange={(data) => onChangePropertyGroup(data, index)}
                  isReadOnly={isReadOnly}
                />
              </Flex>
            </>
          )
      )}
    </>
  );
}

export default function MarketingEventFilterGroup({
  data,
  id,
  onChange,
  onRemove,
  isReadOnly,
}: {
  data: DynamicListChild;
  id: string;
  onChange: (data: any) => void;
  onRemove: () => void;
  isReadOnly?: boolean;
}) {
  const {
    dynamicList: {
      marketingEventNamesList,
      operators: { data: operators },
      operatorsArgCount,
    },
  } = useSelector(selectCampaign);
  const { activeErrorCheck } = useContext(DynamicListContext);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getMarketingActivities());
  }, [dispatch]);

  function onActivityChange(value: string) {
    let dataCopy = cloneDeep(data);
    // reset data in property groups when filter group activity changes
    if (dataCopy.children[0].value?.[0] !== value) {
      dataCopy.children = dataCopy.children.filter(
        (child: DynamicListChildL1) => child.type !== TYPE.GROUP
      );
    }
    dataCopy.children[0].value = [value];
    if (value) {
      dataCopy.children[0].validation_error = "";
    }
    onChange(dataCopy);
  }

  function onRemoveRow(index: number) {
    const dataCopy = cloneDeep(data);
    dataCopy.children = dataCopy.children.filter((_, i) => i !== index);
    onChange(dataCopy);
  }

  function onAddLinkFilter() {
    const dataCopy = cloneDeep(data);
    dataCopy.children.splice(1, 0, {
      type: TYPE.EXPRESSION,
      filter_type: FILTER_TYPE.MARKETING_ACTIVITY,
      filter: CHILD_FILTER_TYPE.LINK_CLICKED,
      property: null,
      operator: null,
      value: [""],
      connector: CONNECTOR.AND,
    });
    onChange(dataCopy);
  }

  function onFilterDataChange(
    field: string,
    index: number,
    value: string | number | CONNECTOR | ValueTypes
  ) {
    const dataCopy = cloneDeep(data);
    switch (field) {
      case "operator":
        dataCopy.children[index].operator = value as string;
        dataCopy.children[index].value = [];
        break;
      case "value":
        dataCopy.children[index].value = value as ValueTypes;
        break;
    }

    const { filterGroup } = validateFilterGroup(
      dataCopy,
      false,
      operatorsArgCount
    );
    onChange(filterGroup);
  }

  const linkFilterRowIndex: number | null = useMemo(() => {
    const hasLinkFilter = isArray(data.children)
      ? data.children.findIndex(
          (child) => child.filter === CHILD_FILTER_TYPE.LINK_CLICKED
        )
      : null;
    return hasLinkFilter && hasLinkFilter >= 0 ? hasLinkFilter : null;
  }, [data]);

  function onPropertyGroupChange(newData: DynamicListChildL1[]) {
    const dataCopy = cloneDeep(data);
    dataCopy.children = newData;
    onChange(dataCopy);
  }

  function onAddPropertyGroup() {
    const dataCopy = cloneDeep(data);
    const newPropertyGroup: DynamicListChildL1 = {
      type: TYPE.GROUP,
      name: "",
      filter_type: null,
      filter: null,
      property: null,
      operator: null,
      value: null,
      children: [
        {
          type: TYPE.EXPRESSION,
          filter_type: FILTER_TYPE.MARKETING_ACTIVITY,
          filter: PROPERTY_FILTER_ID.CAMPAIGN,
          property: null,
          operator: null,
          value: [""],
          connector: CONNECTOR.AND,
        },
      ],
      connector: CONNECTOR.AND,
    };
    const templatePropertyGroupChild: DynamicListChildL2 = {
      type: TYPE.EXPRESSION,
      filter_type: FILTER_TYPE.MARKETING_ACTIVITY,
      filter: PROPERTY_FILTER_ID.TEMPLATE,
      property: null,
      operator: null,
      value: [""],
      connector: CONNECTOR.AND,
    };
    const reasonPropertyGroupChild: DynamicListChildL2 = {
      type: TYPE.EXPRESSION,
      filter_type: FILTER_TYPE.MARKETING_ACTIVITY,
      filter: PROPERTY_FILTER_ID.REASON,
      property: null,
      operator: null,
      value: [""],
      connector: CONNECTOR.AND,
    };
    if (getEventList()?.includes(PROPERTY_FILTER_ID.TEMPLATE)) {
      newPropertyGroup?.children?.push(templatePropertyGroupChild);
    }
    if (getEventList()?.includes(PROPERTY_FILTER_ID.REASON)) {
      newPropertyGroup?.children?.push(reasonPropertyGroupChild);
    }

    dataCopy.children.push(newPropertyGroup);
    onChange(dataCopy);
  }

  function getEventList() {
    return data.children[0].value?.[0]
      ? marketingEventNamesList.data
          ?.find((x) => x.id === data.children[0].value?.[0])
          ?.arguments.map((x) => x.id)
      : [];
  }

  const wrapperStyle = isReadOnly
    ? {
        px: "0",
        py: "0",
        spacing: "1",
      }
    : {
        px: "3",
        py: "5",
        spacing: "4",
      };

  return (
    <FilterGroupBox
      id={id}
      onRemove={onRemove}
      icon={RiMailCheckLine}
      isReadOnly={isReadOnly}
      rounded="lg"
      borderColor="gray.200"
      borderWidth="1px"
      bg="white"
    >
      <VStack alignItems="flex-start" {...wrapperStyle}>
        {data.children && (
          <>
            <HStack
              width="100%"
              wrap="wrap"
              gridGap={isReadOnly ? undefined : "2"}
            >
              <ActivitySelector
                marketingActivityList={marketingEventNamesList.data || []}
                value={data.children[0].value?.[0] as string}
                onChange={(value) => onActivityChange(value)}
                validationError={
                  activeErrorCheck ? data.children[0].validation_error : ""
                }
                isReadOnly={isReadOnly}
              />

              {!isReadOnly &&
                getEventList()?.includes(CHILD_FILTER_TYPE.LINK_CLICKED) &&
                !linkFilterRowIndex && (
                  <IButton
                    hidden={!data.children?.[0]?.value?.[0]}
                    size="sm"
                    leftIcon={<FaPlus fontSize="12px" />}
                    onClick={onAddLinkFilter}
                    variant="link"
                  >
                    link
                  </IButton>
                )}
            </HStack>

            {getEventList()?.includes(CHILD_FILTER_TYPE.LINK_CLICKED) &&
              linkFilterRowIndex &&
              operators && (
                <>
                  {!isReadOnly && <Divider />}
                  <LinkFilter
                    stringOperators={operators[OperatorType.STRING]}
                    setOperator={(value) =>
                      onFilterDataChange("operator", linkFilterRowIndex, value)
                    }
                    operator={data.children[linkFilterRowIndex]?.operator}
                    value={
                      data.children[linkFilterRowIndex]?.value as ValueTypes
                    }
                    setValue={(value) =>
                      onFilterDataChange("value", linkFilterRowIndex, value)
                    }
                    showRemoveButton={true}
                    onRemoveRow={() => onRemoveRow(linkFilterRowIndex)}
                    validationError={
                      activeErrorCheck
                        ? data.children[linkFilterRowIndex]?.validation_error
                        : ""
                    }
                    isReadOnly={isReadOnly}
                  />
                </>
              )}
            <PropertyGroupContainer
              data={data.children}
              onChange={onPropertyGroupChange}
              isReadOnly={isReadOnly}
            />
            {!isReadOnly && (
              <IButton
                size="sm"
                variant="link"
                leftIcon={<FaPlus fontSize="12px" />}
                onClick={onAddPropertyGroup}
                isDisabled={!data.children?.[0]?.value?.[0]}
              >
                Add new property
              </IButton>
            )}
          </>
        )}
      </VStack>
    </FilterGroupBox>
  );
}
